import React, { useCallback, useState } from 'react'

import { TeamOutlined } from '@ant-design/icons'
import { Avatar, Button, Col, Divider, List, notification, Space, Typography } from 'antd'

import { Permission, Team } from '@pollination-solutions/pollination-sdk'

import { Empty, InputLabel } from 'atoms'
import DeleteButton from 'atoms/DeleteButton'
import SelectPermission from 'atoms/SelectPermission'
import { TeamPermission } from 'hooks/useLicenseTeams'
import { SearchTeams } from 'molecules/SearchTeams/SearchTeams'

interface CreateTeamPayload {
  name: string
  description?: string
}

export interface LicensePoolTeamAccessListProps {
  loading: boolean,
  teamPermissions: TeamPermission[]
  canAdd: boolean
  permissionOptions: Permission[]
  searchTeams: (search?: string) => Promise<Team[]>
  upsertTeamPermission: (team: Team, permission: Permission) => Promise<void>
  removeTeamPermission: (team: Team) => Promise<void>
  createTeam?: (createTeam: CreateTeamPayload) => Promise<Team | undefined>
}

export const ResourceTeamAccessList: React.FunctionComponent<LicensePoolTeamAccessListProps> = ({
  loading,
  teamPermissions,
  canAdd,
  permissionOptions,
  searchTeams,
  upsertTeamPermission,
  removeTeamPermission,
  createTeam,
}) => {

  const [teamToAdd, setTeamToAdd] = useState<Team | undefined>()
  const [permissionToAdd, setPermissionToAdd] = useState<Permission>(permissionOptions[0])
  const [upsertPermissionLoading, setUpsertPermissionLoading] = useState<boolean>(false)

  const upsertPermission = useCallback(() => {
    if (!teamToAdd) return
    setUpsertPermissionLoading(true)
    upsertTeamPermission(teamToAdd, permissionToAdd)
      .then(() => {
        setTeamToAdd(undefined)
      })
      .catch(error => {
        notification.error({
          message: error.data.details
        })
      })
      .finally(() => setUpsertPermissionLoading(false))
  }, [teamToAdd, permissionToAdd, upsertTeamPermission])

  const removePermission = useCallback((team: Team) => {
    return removeTeamPermission(team)
      .then(() => {
        setTeamToAdd(undefined)
      })
      .catch(error => {
        notification.error({
          message: error.data.details
        })
      })
  }, [removeTeamPermission])

  return (
    <List
      style={{ width: '100%' }}
      loading={loading}
      dataSource={teamPermissions}
      locale={{
        emptyText: <Empty
          description='No teams have access to this resource.'
        />
      }}
      header={
        <Col span={24}>
          <Space style={{ width: '100%' }} direction='vertical'>
            <Typography.Text>Teams who have access.</Typography.Text>
          </Space>
        </Col>
      }
      footer={
        canAdd &&
        <InputLabel label='Add Teams' >
          <Space align='center'>
            <Button icon={<TeamOutlined />} shape='circle'
              disabled={!teamToAdd}
              loading={upsertPermissionLoading}
              type='primary'
              onClick={upsertPermission} />
            <Divider type='vertical' />
            {!upsertPermissionLoading && 
              <SearchTeams 
                searchTeams={searchTeams} 
                selectTeam={setTeamToAdd} 
                createTeam={createTeam} />}
            {permissionOptions.length > 1 &&
              <SelectPermission 
                permissionOptions={permissionOptions} 
                value={permissionToAdd} 
                onChange={(value) => Promise.resolve(setPermissionToAdd(value))} />
            }
          </Space>
        </InputLabel>
      }
      renderItem={(teamPermission) =>
        <>
          <List.Item actions={[
            permissionOptions.length > 1 ? <SelectPermission key={teamPermission.team.id} permissionOptions={permissionOptions} value={teamPermission.permission} onChange={(value) => upsertTeamPermission(teamPermission.team, value)} /> : <></>,
            <Button type='link' key={teamPermission.team.id} href={teamPermission.team.link}>Edit</Button>,
            <DeleteButton key={teamPermission.team.id} title={'Remove Access'} onClick={() => removePermission(teamPermission.team)} />
          ]}>
            <List.Item.Meta
              title={teamPermission.team.name}
              description={teamPermission.team.description}
              avatar={<Avatar.Group maxCount={3}>
                {teamPermission.team.members.map(member => (
                  <Avatar key={member.user.username} src={member.user.picture} />
                ))}
              </Avatar.Group>}
            />
          </List.Item>
        </>
      }
    />
  )
}
